import { useCallback, useMemo, useState } from 'react';
import { generatePath, useMatch, useNavigate, useResolvedPath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaRegTrashAlt } from 'react-icons/fa';
import { CompanyReadModel, PermissionKeys } from '@client/api';
import { Button, isDefinedString, isNotNullish, TableCell, TableRow, Text } from '@client/toolkit';

import { CompaniesPaths } from '../../companies.router';
import { CompanyDeleteModal } from '../..';
import { PermissionBasedActionButton } from '../../../../components/permission-based-button/permission-based-button';

type CompanyListRowProps = {
  company?: CompanyReadModel;
  isFetching?: boolean;
};

const ROLE_TRANSLATION_KEYS: Record<string, string> = {
  Manager: 'roles.manager',
  Owner: 'roles.owner',
  Tenant: 'roles.tenant',
};

export function CompanyListRow({ company, isFetching }: CompanyListRowProps) {
  const { t } = useTranslation('companies');
  const companyPath = useResolvedPath(CompaniesPaths.Company);
  const [deleting, setDeleting] = useState(false);
  const companyPathMatch = useMatch<'id', string>({ path: companyPath.pathname });
  const selectedCompanyId = useMemo(
    () => (isNotNullish(companyPathMatch) ? companyPathMatch.params.id : null),
    [companyPathMatch],
  );

  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    if (!company?.id) return;

    if (company.id === selectedCompanyId) return navigate(generatePath(CompaniesPaths.Companies));

    navigate(generatePath(CompaniesPaths.Company, { id: company.id }));
  }, [navigate, company?.id, selectedCompanyId]);

  return (
    <TableRow hover={company?.id != null} selected={selectedCompanyId === company?.id} onClick={handleClick}>
      <TableCell className="align-middle">
        <Text isEmpty={!isDefinedString(company?.name)} loading={isFetching}>
          <strong>{company?.name}</strong>
        </Text>
      </TableCell>
      <TableCell className="align-middle">
        <Text isEmpty={!company?.businessAddress} loading={isFetching} className="text-secondary">
          {company?.businessAddress
            ? `${company.businessAddress.address ?? ''}, ${company.businessAddress.postalCode ?? ''} ${
                company.businessAddress.city ?? ''
              }`.replace(/^, /, '')
            : ''}
        </Text>
      </TableCell>
      <TableCell className="align-middle">
        <Text isEmpty={!company?.roles?.length} loading={isFetching} className="text-secondary">
          {company?.roles?.map((role) => t(ROLE_TRANSLATION_KEYS[role] ?? role)).join(', ')}
        </Text>
      </TableCell>
      <TableCell>
        <PermissionBasedActionButton
          color="primary-dark"
          size="xs"
          onClick={(e) => {
            e.stopPropagation();
            setDeleting(true);
          }}
          disabled={isFetching}
          permissions={[PermissionKeys.Tenant.CompanyDelete]}
        >
          <FaRegTrashAlt className="text-brand-primary" />
        </PermissionBasedActionButton>
      </TableCell>
      <CompanyDeleteModal
        company={company ?? null}
        open={deleting}
        onCancel={() => setDeleting(false)}
        onDelete={() => navigate(generatePath(CompaniesPaths.Companies))}
      />
    </TableRow>
  );
}
